import axios from 'axios';

import { toast } from '@/utils/toast.ts';

type ExtractType =
  | 'vessel'
  | 'quotation'
  | 'invoice'
  | 'equipment'
  | 'maintenance'
  | 'itinerary'
  | 'event'
  | 'speech';
type ExtractMethods =
  | 'extract'
  | 'identify'
  | 'create'
  | 'update'
  | 'suggest-equipment-deep'
  | 'apply-equipment'
  | 'equipment-chat'
  | 'suggest-maintenance'
  | 'update-maintenance'
  | 'chat-maintenance'
  | 'apply-maintenance'
  | 'wizard'
  | 'suggest'
  | 'questionnaire/autofill'
  | 'cover/generate'
  | 'plan/generate'
  | 'plan/refine'
  | 'transcribe';

export type AIErrorInfo = {
  code?: string;
  message: string;
  status?: number;
  retryable: boolean;
};

const RETRYABLE_STATUSES = [429, 502, 503, 504];

export const parseAIError = (error: unknown): AIErrorInfo => {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const retryable = typeof status === 'number' && RETRYABLE_STATUSES.includes(status);
    const data = error.response?.data as
      | { error?: { code?: string; message?: string }; detail?: unknown }
      | undefined;

    if (data?.error) {
      return {
        code: data.error.code,
        message: data.error.message || 'Couldn’t get a reply. Please try again.',
        status,
        retryable,
      };
    }

    if (typeof data?.detail === 'string') {
      return { message: data.detail, status, retryable };
    }

    return { message: 'Couldn’t get a reply. Please try again.', status, retryable };
  }

  return { message: 'Couldn’t get a reply. Please try again.', retryable: false };
};

type ExtractAIProps = {
  formData: FormData | Record<string, unknown>;
  type: ExtractType;
  method: ExtractMethods;
  isJson?: boolean;
  signal?: AbortSignal;
  returnErrorResponse?: boolean;
  suppressErrorToast?: boolean;
};

export const extractAI = async ({
  formData,
  type,
  method,
  isJson,
  signal,
  returnErrorResponse,
  suppressErrorToast,
}: ExtractAIProps) => {
  const link = `${import.meta.env.VITE_APP_EXTRACT_URL}/api/ai/${type}/${method}`;

  return await axios
    .post(link, formData, {
      headers: isJson
        ? { 'Content-Type': 'application/json' }
        : { 'Content-Type': 'multipart/form-data' },
      signal,
    })
    .catch((error) => {
      if (axios.isCancel(error) || (error as Error)?.name === 'CanceledError') {
        return undefined;
      }

      const aiError = parseAIError(error);

      if (!suppressErrorToast) {
        toast.error(aiError.message);
      }

      if (returnErrorResponse && axios.isAxiosError(error) && error.response) {
        return error.response;
      }

      return undefined;
    });
};
